// Minimal fetch helpers for the app's raw-JSON endpoints (autosave, lock
// claim/heartbeat). Everything else goes through Inertia's router — see
// CLAUDE.md's Inertia response rules. Client-only: call from handlers and
// effects, never during render (SSR has no document).

export function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
}

export type JSONResult<T> = { status: number; data: T | null }

// GET for raw-JSON reads (catalog search). AbortSignal support lets callers
// cancel stale in-flight searches — debounce alone can't stop an out-of-order
// response from clobbering newer results.
export async function getJSON<T>(
  url: string,
  opts: { signal?: AbortSignal } = {}
): Promise<JSONResult<T>> {
  const response = await fetch(url, {
    headers: { Accept: "application/json" },
    signal: opts.signal,
  })
  let data: T | null = null
  try {
    data = (await response.json()) as T
  } catch {
    data = null
  }
  return { status: response.status, data }
}

// keepalive lets the final flush survive pagehide/unmount (and unlike
// sendBeacon it can carry the CSRF + lock-token headers).
export async function postJSON<T>(
  url: string,
  body: unknown,
  opts: { keepalive?: boolean; headers?: Record<string, string> } = {}
): Promise<JSONResult<T>> {
  return requestJSON<T>("POST", url, body, opts)
}

/** PATCH/DELETE sibling of postJSON, for raw-JSON CRUD (add-on rules). */
export async function requestJSON<T>(
  method: "POST" | "PATCH" | "DELETE",
  url: string,
  body: unknown,
  opts: { keepalive?: boolean; headers?: Record<string, string> } = {}
): Promise<JSONResult<T>> {
  const response = await fetch(url, {
    method,
    keepalive: opts.keepalive ?? false,
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      ...opts.headers,
    },
    body: JSON.stringify(body),
  })
  let data: T | null = null
  try {
    data = (await response.json()) as T
  } catch {
    data = null
  }
  return { status: response.status, data }
}
